import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, Unlock, FileSpreadsheet, FileDown } from "lucide-react";
import { Sheet, MONTHS, downloadPdf } from "@/components/MonthlyStatement";

const pad = (n) => String(n).padStart(2, "0");
const eur = (v) => v.toLocaleString("it-IT", { maximumFractionDigits: 2 });

function StatementCard({ s, onUnlock }) {
  const [open, setOpen] = useState(false);
  const [y, m] = s.month.split("-").map(Number);
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid={`statement-card-${s.id}`}>
      <button className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left" onClick={() => setOpen(!open)} data-testid={`statement-toggle-${s.id}`}>
        <span className="font-semibold flex-1 min-w-[140px]">{s.technician_name}</span>
        <span className="text-xs text-slate-400">{s.totals.days} righe · <span className="text-cyan-300">{eur(s.totals.hours)} ore</span></span>
        <span className="text-sm font-bold text-emerald-300" data-testid={`statement-pay-${s.id}`}>€ {eur(s.totals.pay)}</span>
        {s.totals.expenses > 0 && <span className="text-xs text-amber-300">+ rimborsi € {eur(s.totals.expenses)}</span>}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <p className="text-xs text-slate-500 mt-1">Inviato il {new Date(s.submitted_at).toLocaleString("it-IT", { timeZone: "Europe/Rome", dateStyle: "short", timeStyle: "short" })}</p>
      {open && (
        <div className="mt-3 space-y-3">
          <Sheet rows={s.rows} y={y} m={m} readOnly />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid={`statement-pdf-${s.id}`} onClick={() => downloadPdf(`/statements/${s.id}/pdf`, `resoconto-${s.month}-${(s.technician_name || "").replace(/\s+/g, "_")}.pdf`)} className="border-slate-700"><FileDown className="w-4 h-4 mr-1" /> Scarica PDF</Button>
            <Button size="sm" variant="outline" data-testid={`statement-unlock-${s.id}`} onClick={() => onUnlock(s)} className="border-slate-700"><Unlock className="w-4 h-4 mr-1" /> Sblocca per correzioni</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function StatementsPanel() {
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [list, setList] = useState(null);
  const load = () => api.get(`/statements?month=${ym}`).then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, [ym]); // eslint-disable-line react-hooks/exhaustive-deps
  const shift = (d) => { const [yy, mm] = ym.split("-").map(Number); const x = new Date(yy, mm - 1 + d, 1); setYm(`${x.getFullYear()}-${pad(x.getMonth() + 1)}`); };
  const unlock = async (s) => {
    if (!window.confirm(`Sbloccare il resoconto di ${s.technician_name}? Potrà correggerlo e inviarlo di nuovo.`)) return;
    try { await api.put(`/statements/${s.id}/unlock`); toast.success("Resoconto sbloccato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [y, m] = ym.split("-").map(Number);
  const total = (list || []).reduce((a, s) => a + s.totals.pay, 0);
  return (
    <div className="space-y-3" data-testid="statements-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-400" /> Resoconti mensili</h2>
        <div className="flex items-center gap-1 ml-auto">
          <Button size="icon" variant="ghost" data-testid="statements-prev-month" onClick={() => shift(-1)}><ChevronLeft className="w-5 h-5" /></Button>
          <span className="text-sm font-semibold w-36 text-center" data-testid="statements-month">{MONTHS[m - 1].toLowerCase()} {y}</span>
          <Button size="icon" variant="ghost" data-testid="statements-next-month" onClick={() => shift(1)}><ChevronRight className="w-5 h-5" /></Button>
        </div>
      </div>
      {list?.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-slate-400 flex-1" data-testid="statements-summary">{list.length} resocont{list.length > 1 ? "i" : "o"} ricevut{list.length > 1 ? "i" : "o"} · totale paghe <b className="text-emerald-300">€ {eur(total)}</b></p>
          <Button size="sm" data-testid="statements-export-month" onClick={() => downloadPdf(`/statements/export/${ym}`, `resoconti-${MONTHS[m - 1].toLowerCase()}-${y}.pdf`)}
            className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold"><FileDown className="w-4 h-4 mr-1.5" /> Scarica tutti (PDF)</Button>
        </div>
      )}
      {list && list.length === 0 && <p className="text-sm text-slate-500">Nessun resoconto ricevuto per questo mese.</p>}
      {list?.map((s) => <StatementCard key={s.id} s={s} onUnlock={unlock} />)}
    </div>
  );
}
